import type { ReactNode } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { AccountPausedScreen } from '../features/auth/AccountPausedScreen';
import { LegalAcceptanceGate } from '../features/legal/LegalAcceptanceGate';
import { TravelBallLegalGate } from '../features/legal/TravelBallLegalGate';
import { AccessDeniedScreen } from './AccessDeniedScreen';
import { PERMISSION_LABELS, hasPermission, type Permission } from '../utils/permissions';
import { hasNoWorkspace, isProfileMissing, profileMissingTarget } from '../utils/profileMissing';
import { isTravelBallCoachKind, isTravelBallFamilyKind } from '../utils/authViewKinds';

type UserRole = 'system_admin' | 'admin' | 'user';

interface ProtectedRouteProps {
  children?: ReactNode;
  /**
   * Roles that are allowed to access this route.
   * If not specified, any authenticated user can access.
   * If specified, user must have one of the listed roles.
   */
  allowedRoles?: UserRole[];
  /**
   * Page permission required to reach this route, withheld per staff member by an organization
   * admin. Checked after `allowedRoles`, and treated as satisfied when the profile carries no
   * permission list at all — see `hasPermission`.
   *
   * Client-side only. The API enforces the same restriction, which is what makes a bookmark or a
   * pasted URL fail rather than merely look tidy.
   */
  requiredPermission?: Permission;
  /**
   * Where to redirect if the user doesn't have the required role.
   * Defaults to '/players'.
   */
  unauthorizedRedirect?: string;
  /**
   * Render `AccessDeniedScreen` in place of a withheld page instead of redirecting to
   * `unauthorizedRedirect`.
   *
   * A behaviour flag, deliberately named as one — the page's own name comes from
   * `PERMISSION_LABELS`, so this carries no data. Opt-in rather than the default because every
   * existing gated route was written expecting the bounce, and flipping all nine is a change to
   * nine features rather than to this one. New gated routes should set it: a silent redirect to
   * /players is indistinguishable from a broken link.
   */
  explainDenial?: boolean;
  /**
   * Phase 1b.5: the workspace kind this route is for. When set, a signed-in user whose active
   * view does not match is bounced to `/` (the landing router), which sends them to their own
   * view's home. Unset = any workspace kind passes (the current default for every route that
   * existed before poly-view).
   *
   * - `college` protects the existing college-side pages (players, calls, schedule, etc.).
   * - `travelball` protects the TB coach pages (program coach and program admin).
   * - `travelball_family` protects the TB family pages (`/travel-ball/family/*`), for player and
   *   parent/guardian views. A family view is never let onto a coach page or the reverse: the API
   *   refuses a family view on every coach route.
   */
  kind?: 'college' | 'travelball' | 'travelball_family';
}

export const ProtectedRoute = ({
  children,
  allowedRoles,
  requiredPermission,
  unauthorizedRedirect = '/players',
  explainDenial = false,
  kind,
}: ProtectedRouteProps) => {
  const {
    user,
    initializing,
    organizationPaused,
    organizationAwaitingPayment,
    legalAcceptanceRequired,
    activeView,
  } = useAuth();

  if (initializing) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        fontSize: '14px',
        color: 'var(--color-text-grey)'
      }}>
        Loading...
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // A Firebase account with no RosterPilot identity at all — same test as before, but now covers
  // "has neither a college seat nor any TB membership." A verified TB signup produces a `views`
  // entry on `/auth/me`, so a TB-only user no longer trips this.
  if (isProfileMissing(user)) {
    return <Navigate to={profileMissingTarget(user)} replace />;
  }

  // A RosterPilot user with no workspace at all (no college seat, program membership or family
  // role): every page would refuse it. `/login` signs it out and says the email is not linked.
  if (hasNoWorkspace(user)) {
    return <Navigate to="/login" replace />;
  }

  // Phase 1b.5: route is for a specific workspace kind, but the user's active view is a different
  // kind — send them to the landing router so they land on their own view's home instead of a
  // page that would 403 every request. A dual-view user wanting to use this page switches view
  // via the switcher first.
  if (kind && activeView && !isMatchingKind(kind, activeView.kind)) {
    return <Navigate to="/" replace />;
  }

  // Organization paused - block the app in place (no redirect, so the user stays
  // signed in and we avoid a redirect loop). System admins are never blocked.
  if (organizationPaused && user.profile?.role !== 'system_admin') {
    return <AccountPausedScreen />;
  }

  // Updated Terms of Service or Privacy Policy not yet accepted - block the app in place behind the
  // acceptance dialog. After the pause (a paused org cannot use the app either way) and before the
  // payment redirect, because the payment page's own requests would be refused until they accept.
  // Keyed by version, so documents that change under a refresh start the dialog over.
  //
  // A travel ball view answers for its travel ball identity and accepts at the travel ball endpoint.
  // The college pause above never covers a travel ball program, so that gate checks the program's
  // own pause first: a paused program shows the paused screen, not the dialog.
  if (legalAcceptanceRequired) {
    const gateKey = `${legalAcceptanceRequired.terms?.version ?? ''}|${legalAcceptanceRequired.privacy?.version ?? ''}`;
    if (activeView && activeView.kind.startsWith('travelball')) {
      return (
        <TravelBallLegalGate
          key={`${activeView.id}|${gateKey}`}
          required={legalAcceptanceRequired}
          checkProgramPause={activeView.kind === 'travelball_org'}
        />
      );
    }
    return <LegalAcceptanceGate key={gateKey} required={legalAcceptanceRequired} />;
  }

  // Organization awaiting its first payment (self-serve signup) - send them to finish paying.
  // After the pause check, matching the API's own gate order. A redirect rather than an in-place
  // screen because /signup/payment is a real page: Stripe's cancel URL lands there too.
  if (organizationAwaitingPayment && user.profile?.role !== 'system_admin') {
    return <Navigate to="/signup/payment" replace />;
  }

  // Check role-based access
  if (allowedRoles && allowedRoles.length > 0) {
    const userRole = user.profile?.role as UserRole | undefined;

    if (!userRole || !allowedRoles.includes(userRole)) {
      return <Navigate to={unauthorizedRedirect} replace />;
    }
  }

  // Check page access withheld by the organization's admin. After the role check, so a role the
  // route does not allow is still reported as a role problem.
  if (requiredPermission && !hasPermission(user.profile, requiredPermission)) {
    return explainDenial ? (
      <AccessDeniedScreen pageName={PERMISSION_LABELS[requiredPermission]} />
    ) : (
      <Navigate to={unauthorizedRedirect} replace />
    );
  }

  // If children provided, render them; otherwise render Outlet for nested routes
  return children ? <>{children}</> : <Outlet />;
};

/**
 * Phase 1b.5: `kind="travelball"` matches the `travelball_org` (program coach or admin) view.
 * `kind="travelball_family"` matches the player and parent/guardian views.
 */
const isMatchingKind = (
  kind: 'college' | 'travelball' | 'travelball_family',
  viewKind: string,
): boolean => {
  if (kind === 'college') return viewKind === 'college';
  if (kind === 'travelball_family') return isTravelBallFamilyKind(viewKind);
  return isTravelBallCoachKind(viewKind);
};
